'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { Loader2 } from 'lucide-react';
interface YandexLoginButtonProps {
  callbackURL?: string;
  onError?: (message: string) => void;
  variant?: 'large' | 'round';
}

/**
 * Кнопка входа через Яндекс OAuth.
 * Использует genericOAuth плагин Better Auth (signIn.oauth2).
 * Скрывается, если NEXT_PUBLIC_YANDEX_CLIENT_ID не задан.
 *
 * variant='large' — полноширинная кнопка с текстом.
 * variant='round' — круглая иконка 48×48 для компактного ряда провайдеров.
 */
export function YandexLoginButton({
  callbackURL = '/',
  onError,
  variant = 'large'
}: YandexLoginButtonProps) {
  const [loading, setLoading] = useState(false);

  // Hide button when Yandex OAuth is not configured (applies to both variants)
  if (!process.env.NEXT_PUBLIC_YANDEX_CLIENT_ID) {
    return null;
  }
  const handleLogin = async () => {
    setLoading(true);
    try {
      const result = await authClient.signIn.oauth2({
        providerId: 'yandex',
        callbackURL
      });
      // If client didn't auto-redirect, handle it manually
      if (result?.data?.url) {
        window.location.href = result.data.url;
      } else if (result?.error) {
        const message = result.error.message || '';
        const isCancelled = message.includes('cancel') || message.includes('denied');
        if (isCancelled) {
          onError?.('Авторизация через Яндекс отменена');
        } else {
          onError?.('Ошибка авторизации через Яндекс. Попробуйте позже');
        }
        setLoading(false);
      } else {
        console.error('[YandexLogin] Unexpected result:', JSON.stringify(result));
      }
    } catch (err) {
      console.error('[YandexLogin] Error:', err);
      onError?.('Ошибка авторизации через Яндекс. Попробуйте позже');
      setLoading(false);
    }
  };
  if (variant === 'round') {
    return <button type="button" onClick={handleLogin} disabled={loading} aria-label="Войти через Яндекс" className="flex items-center justify-center w-12 h-12 rounded-full bg-white hover:bg-gray-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 disabled:opacity-50 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-zinc-700 transition-colors cursor-pointer disabled:cursor-not-allowed">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <YandexIcon className="w-[31px] h-[31px]" />}
      </button>;
  }
  return <button onClick={handleLogin} disabled={loading} className="flex items-center justify-center gap-3 w-full h-[52px] px-5 bg-white hover:bg-slate-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 disabled:opacity-50 text-slate-800 dark:text-zinc-100 border border-slate-200 dark:border-zinc-700 rounded-2xl text-[15px] font-medium tracking-tight shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] hover:border-slate-300 dark:hover:border-zinc-600 transition-all cursor-pointer disabled:cursor-not-allowed">
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <YandexIcon className="w-5 h-5" />}
      Войти через Яндекс
    </button>;
}
function YandexIcon(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <circle cx="12" cy="12" r="12" fill="#FC3F1D" />;
    t2 = <path d="M13.32 7.666h-.924c-1.694 0-2.585.858-2.585 2.123 0 1.43.616 2.09 1.881 2.948l1.045.715-3.003 4.548H7.856l2.695-4.07c-1.54-1.1-2.387-2.156-2.387-3.938 0-2.2 1.54-3.663 4.191-3.663h2.31V18h-1.342V7.666z" fill="white" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = <svg className={className} viewBox="0 0 24 24" fill="none">{t1}{t2}</svg>;
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}